import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaExclamationTriangle } from 'react-icons/fa';

/**
 * MatchDetailsPopup Component
 * 
 * Reusable popup for displaying detailed lender match criteria
 * Extracted from LenderMatchScore component for use in enhanced submit buttons
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the popup
 * @param {Function} props.onClose - Function to close the popup
 * @param {string} props.lenderName - Name of the lender
 * @param {number} props.score - Match score (0-100)
 * @param {Array} props.matchedCriteria - Array of matched criteria objects
 * @param {Array} props.missedCriteria - Array of missed criteria objects
 * @param {Array} props.incompleteCriteria - Array of incomplete criteria objects
 * @param {boolean} props.isAutoDecline - Whether this is an auto-decline scenario
 * @returns {JSX.Element} MatchDetailsPopup component
 */
const MatchDetailsPopup = ({
  show,
  onClose,
  lenderName,
  score,
  matchedCriteria = [],
  missedCriteria = [],
  incompleteCriteria = [],
  isAutoDecline = false
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Get score variant for styling
  const getScoreVariant = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'info';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const scoreVariant = getScoreVariant(score);

  // Handle animation timing
  useEffect(() => {
    if (show) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Handle closing with animation
  const handleClose = () => {
    if (isClosing) return;
    
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  // Helper to format criteria values for display
  const formatCriterionValue = (criterion) => {
    if (typeof criterion.value === 'undefined' || criterion.value === null) {
      return 'Not specified';
    }
    
    // Special handling for arrays (used by Industry with is_not operator)
    if (Array.isArray(criterion.value)) {
      if (criterion.value.length === 0) {
        return 'None selected';
      }
      return criterion.value.join(', ');
    }
    
    if (criterion.fieldName && criterion.fieldName.includes('Date')) {
      return criterion.value;
    }
    
    if (criterion.fieldName && (criterion.fieldName.includes('Revenue') || 
        criterion.fieldName.includes('Amount') || 
        criterion.fieldName.includes('Balance'))) {
      return typeof criterion.value === 'number'
        ? `$${criterion.value.toLocaleString()}`
        : `$${criterion.value}`;
    }
    
    return criterion.value.toString();
  };

  // Format currency values
  const formatCurrency = (value) => {
    return `$${value.toLocaleString()}`;
  };

  // Format any value based on field type
  const formatValue = (fieldName, value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not provided';
    }

    // Check if it's a state field
    if (fieldName === 'Business State' || fieldName === 'Contact State' || 
        fieldName === 'Secondary Contact State' || fieldName === 'BusinessState' || 
        fieldName === 'ContactState' || fieldName === 'Contact2State') {
      return value;
    }

    // Format currency values
    if (fieldName.includes('Amount') || fieldName.includes('Revenue') || 
        fieldName.includes('Deposit') || fieldName.includes('Debt')) {
      if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        return formatCurrency(parseFloat(value));
      } else if (typeof value === 'number') {
        return formatCurrency(value);
      }
      return value;
    }

    // Format date values
    if (fieldName.includes('Date')) {
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }

    // Format credit score values
    if (fieldName.includes('Credit Score') || fieldName.includes('FICO')) {
      if (typeof value === 'string' && !isNaN(parseInt(value))) {
        return parseInt(value);
      }
      if (typeof value === 'number') {
        return Math.round(value);
      }
    }

    // Format time in business
    if (fieldName.includes('Time In Business') || fieldName.includes('Years in Business')) {
      if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        const years = parseFloat(value);
        return years === 1 ? '1 year' : `${years} years`;
      }
      if (typeof value === 'number') {
        return value === 1 ? '1 year' : `${value} years`;
      }
    }

    return value;
  };

  // Get actual value for a criterion
  const getActualValue = (criterion) => {
    if (criterion.actualValue !== undefined) {
      return formatValue(criterion.fieldName, criterion.actualValue);
    }
    return 'Not available';
  };

  // Check for missed required criteria (which would cause auto-decline)
  const missedRequiredCriteria = missedCriteria.filter(criterion => criterion.required);

  // Render criteria content
  const renderCriteriaContent = () => {
    const hasCriteria = matchedCriteria.length > 0 || missedCriteria.length > 0 || incompleteCriteria.length > 0;

    return (
      <>
        {!hasCriteria ? (
          <div className="text-center text-muted p-3" style={{ fontSize: '0.9rem' }}>
            <FaExclamationTriangle className="me-2" size="1em" />
            No criteria have been defined for this lender yet.
          </div>
        ) : (
          <>
            {/* Auto-decline warning message */}
            {isAutoDecline && (
              <div className="bg-danger bg-opacity-10 p-3 border-bottom">
                <div className="d-flex align-items-center mb-2">
                  <FaExclamationTriangle className="text-danger me-2" size="1em" />
                  <span className="fw-bold text-danger">Auto-Decline: Required Criteria Not Met</span>
                </div>
                <div className="d-flex flex-wrap">
                  {missedRequiredCriteria.map((criterion, index) => (
                    <div key={index} className="criterion-card me-2 mb-2" style={{
                      flex: '1 1 300px',
                      minWidth: '250px',
                      maxWidth: '400px',
                      padding: '10px',
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(220, 53, 69, 0.05)'
                    }}>
                      <div className="criterion-header d-flex align-items-start">
                        <FaTimesCircle className="text-danger me-2 mt-1" size="0.8em" />
                        <div className="criterion-content" style={{ width: '100%' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-medium">{criterion.fieldName}*</span>
                            <span className="text-muted small"><strong>{criterion.operator}</strong> {formatCriterionValue(criterion)}</span>
                          </div>
                          <div className="status text-danger mt-1" style={{ fontSize: '0.85rem' }}>
                            Current: {getActualValue(criterion)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          
            {/* Matched criteria section */}
            <div className="p-3 border-bottom">
              <div className="d-flex align-items-center mb-2">
                <FaCheckCircle className="text-success me-2" size="0.9em" />
                <span className="fw-bold text-success">Matched Criteria ({matchedCriteria.length})</span>
              </div>
              
              {matchedCriteria.length > 0 ? (
                <div className="d-flex flex-wrap">
                  {matchedCriteria.map((criterion, index) => (
                    <div key={index} className="criterion-card me-2 mb-2" style={{
                      flex: '1 1 300px',
                      minWidth: '250px',
                      maxWidth: '400px',
                      padding: '10px',
                      border: '1px solid rgba(40, 167, 69, 0.2)',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(40, 167, 69, 0.05)'
                    }}>
                      <div className="criterion-header d-flex justify-content-between align-items-center">
                        <span className="fw-medium">{criterion.fieldName}{criterion.required ? '*' : ''}</span>
                        <span className="text-muted small"><strong>{criterion.operator}</strong> {formatCriterionValue(criterion)}</span>
                      </div>
                      <div className="status text-success mt-1" style={{ fontSize: '0.85rem' }}>
                        Current: {getActualValue(criterion)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted ps-4">No matched criteria.</div>
              )}
            </div>
            
            {/* Missed criteria section */}
            <div className="p-3 border-bottom">
              <div className="d-flex align-items-center mb-2">
                <FaTimesCircle className="text-danger me-2" size="0.9em" />
                <span className="fw-bold text-danger">Missed Criteria ({missedCriteria.length})</span>
              </div>
              
              {missedCriteria.length > 0 ? (
                <div className="d-flex flex-wrap">
                  {missedCriteria.map((criterion, index) => (
                    <div key={index} className="criterion-card me-2 mb-2" style={{
                      flex: '1 1 300px',
                      minWidth: '250px',
                      maxWidth: '400px',
                      padding: '10px',
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(220, 53, 69, 0.05)'
                    }}>
                      <div className="criterion-header d-flex justify-content-between align-items-center">
                        <span className="fw-medium">{criterion.fieldName}{criterion.required ? '*' : ''}</span>
                        <span className="text-muted small"><strong>{criterion.operator}</strong> {formatCriterionValue(criterion)}</span>
                      </div>
                      <div className="status text-danger mt-1" style={{ fontSize: '0.85rem' }}>
                        Current: {getActualValue(criterion)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted ps-4">No missed criteria.</div>
              )}
            </div>
            
            {/* Incomplete criteria section */}
            {incompleteCriteria.length > 0 && (
              <div className="p-3 border-bottom">
                <div className="d-flex align-items-center mb-2">
                  <FaHourglassHalf className="text-warning me-2" size="0.9em" />
                  <span className="fw-bold text-warning">Pending Input ({incompleteCriteria.length})</span>
                </div>
                
                <div className="d-flex flex-wrap">
                  {incompleteCriteria.map((criterion, index) => (
                    <div key={index} className="criterion-card me-2 mb-2" style={{
                      flex: '1 1 300px',
                      minWidth: '250px',
                      maxWidth: '400px',
                      padding: '10px',
                      border: '1px solid rgba(255, 193, 7, 0.2)',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(255, 193, 7, 0.05)'
                    }}>
                      <div className="criterion-header d-flex justify-content-between align-items-center">
                        <span className="fw-medium">{criterion.fieldName}{criterion.required ? '*' : ''}</span>
                        <span className="text-muted small"><strong>{criterion.operator}</strong> {formatCriterionValue(criterion)}</span>
                      </div>
                      <div className="status text-warning mt-1" style={{ fontSize: '0.85rem' }}>
                        (needs input)
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Explanation of asterisk */}
            <div className="p-3 pt-2 border-top">
              <small className="text-muted">* Required criteria</small>
            </div>
          </>
        )}
      </>
    );
  };

  if (!show) return null;

  return (
    <div 
      className="custom-modal-backdrop" 
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: `rgba(0, 0, 0, ${isClosing ? 0.1 : 0.5})`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1050,
        transition: 'background-color 0.2s ease',
        opacity: isAnimating ? 0.95 : 1
      }}
    >
      <div 
        className="custom-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '900px',
          maxWidth: '95vw',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${isClosing ? 0.95 : isAnimating ? 0.98 : 1})`,
          transformOrigin: 'center',
          transition: 'transform 0.2s ease',
          zIndex: 1060,
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          opacity: isAnimating ? 0.95 : 1
        }}
      >
        <div className={`custom-modal-header bg-${isAutoDecline ? 'danger' : scoreVariant}`} style={{
          padding: '0.75rem 1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <h6 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
            {lenderName} - {isAutoDecline ? 'Auto-Decline' : `${score}% Match Score`}
          </h6>
          <button 
            type="button" 
            className="btn-close btn-close-white"
            onClick={handleClose}
            aria-label="Close"
          ></button>
        </div>
        <div className="custom-modal-body" style={{
          padding: 0,
          maxHeight: '70vh',
          overflowY: 'auto'
        }}>
          {renderCriteriaContent()}
        </div>
      </div>
    </div>
  );
};

export default MatchDetailsPopup; 